import "./styles.scss";

import { Box, Grid, Skeleton } from "@mui/material";

const fieldSkeleton = (key) => (
  <Box key={key} sx={{ mb: 1.2 }}>
    <Skeleton variant="text" width="34%" height={20} />
    <Skeleton variant="rounded" height={42} sx={{ borderRadius: "8px" }} />
  </Box>
);

const ProfileSidebarSkeleton = () => (
  <Box className="edit-user-details-skeleton__profile-sidebar">
    <Box className="edit-user-details-skeleton__badge-card">
      <Box className="edit-user-details-skeleton__badge-header">
        <Skeleton variant="text" width="58%" height={44} />
        <Skeleton variant="circular" width={24} height={24} />
      </Box>
      <Box className="edit-user-details-skeleton__achievement-row">
        <Skeleton variant="circular" width={54} height={54} />
        <Skeleton variant="text" width="54%" height={28} />
      </Box>
    </Box>

    <Box className="edit-user-details-skeleton__badge-card edit-user-details-skeleton__badge-card--progress">
      <Box className="edit-user-details-skeleton__badge-header">
        <Skeleton variant="text" width="78%" height={44} />
        <Skeleton variant="circular" width={24} height={24} />
      </Box>
      {[0, 1, 2].map((row) => (
        <Box
          className="edit-user-details-skeleton__progress-row"
          key={`profile-route-badge-row-${row}`}
        >
          <Skeleton variant="circular" width={48} height={48} />
          <Box className="edit-user-details-skeleton__progress-copy">
            <Skeleton variant="text" width="68%" height={22} />
            <Skeleton variant="rounded" width="82%" height={7} />
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const ProfileRouteSkeleton = () => (
  <Box className="edit-user-details-page edit-user-details-skeleton edit-user-details-skeleton--profile-route">
    <Box className="edit-user-details-shell">
      <Box className="learner-edit-form-page">
        <Box className="edit-user-details-skeleton__profile-layout">
          <Box className="edit-user-details-skeleton__profile-main">
            <Box className="learner-edit-hero edit-user-details-skeleton__hero">
              <Skeleton
                variant="rounded"
                className="learner-edit-hero__cover edit-user-details-skeleton__cover"
              />

              <Box className="learner-edit-hero__profile edit-user-details-skeleton__profile">
                <Skeleton
                  variant="circular"
                  className="learner-edit-hero__avatar edit-user-details-skeleton__avatar"
                />
                <Box className="edit-user-details-skeleton__identity">
                  <Skeleton variant="text" width={280} height={56} />
                  <Skeleton variant="text" width={200} height={28} />
                  <Box className="edit-user-details-skeleton__stats">
                    <Skeleton variant="text" width={56} height={26} />
                    <Skeleton variant="text" width={86} height={26} />
                    <Skeleton variant="text" width={112} height={26} />
                  </Box>
                  <Skeleton variant="rounded" width={150} height={50} />
                </Box>
                <Box className="edit-user-details-skeleton__sponsor">
                  <Skeleton variant="rounded" width={152} height={48} />
                  <Skeleton variant="text" width={90} height={22} />
                </Box>
              </Box>
            </Box>

            <Box className="edit-user-details-skeleton__profile-sections">
              {[0, 1].map((section) => (
                <Box
                  className="edit-user-details-skeleton__profile-section"
                  key={`profile-route-section-${section}`}
                >
                  <Box className="edit-user-details-skeleton__section-header">
                    <Skeleton variant="text" width={section === 0 ? 260 : 150} height={48} />
                    <Skeleton variant="circular" width={24} height={24} />
                  </Box>
                  <Skeleton variant="text" width="40%" height={24} />
                  <Skeleton variant="rounded" width="90%" height={54} />
                  <Skeleton variant="text" width="40%" height={24} />
                </Box>
              ))}
            </Box>
          </Box>

          <ProfileSidebarSkeleton />
        </Box>
      </Box>
    </Box>
  </Box>
);

const EditUserDetailsSkeleton = ({ profileRoute = false }) => {
  if (profileRoute) {
    return <ProfileRouteSkeleton />;
  }

  return (
    <Box className="edit-user-details-page edit-user-details-skeleton">
      <Box className="edit-user-details-shell">
        <Box className="learner-edit-form-page">
          <Box className="learner-edit-hero edit-user-details-skeleton__hero">
            <Skeleton
              variant="rounded"
              className="learner-edit-hero__cover edit-user-details-skeleton__cover"
            />

            <Box className="learner-edit-hero__profile edit-user-details-skeleton__profile">
              <Skeleton
                variant="circular"
                className="learner-edit-hero__avatar edit-user-details-skeleton__avatar"
              />
              <Box className="edit-user-details-skeleton__identity">
                <Skeleton variant="text" width={280} height={56} />
                <Skeleton variant="text" width={200} height={28} />
              </Box>
            </Box>
          </Box>

          <Box className="learner-edit-tabs edit-user-details-skeleton__tabs">
            <Skeleton variant="rounded" width={120} height={38} />
            <Skeleton variant="rounded" width={130} height={38} />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8} lg={9}>
              <Box className="learner-edit-main-card">
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Skeleton variant="text" width="48%" height={58} sx={{ mb: 0.8 }} />
                    {[...Array(11)].map((_, idx) => fieldSkeleton(`left-field-${idx}`))}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Skeleton variant="text" width="52%" height={58} sx={{ mb: 0.8 }} />
                    <Box
                      className="profile-upload-card"
                      sx={{
                        minHeight: 360,
                        border: "1px solid rgba(15, 23, 42, 0.1)",
                        borderRadius: "16px",
                        background: "#fafafa",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Skeleton variant="circular" width={190} height={190} sx={{ mb: 1.2 }} />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Skeleton variant="rounded" width={86} height={34} />
                        <Skeleton variant="rounded" width={86} height={34} />
                      </Box>
                    </Box>

                    <Box sx={{ mb: 1.1 }}>
                      <Skeleton variant="text" width="20%" height={22} />
                      <Skeleton variant="rounded" height={130} sx={{ borderRadius: "8px" }} />
                    </Box>

                    {[...Array(2)].map((_, idx) => fieldSkeleton(`right-field-${idx}`))}

                    <Skeleton variant="text" width="38%" height={34} sx={{ mt: 0.5, mb: 0.5 }} />
                    <Skeleton variant="rounded" height={42} sx={{ borderRadius: "8px", mb: 0.8 }} />

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Skeleton variant="text" width="42%" height={24} />
                      <Skeleton variant="text" width={52} height={24} />
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.7 }}>
                      {[...Array(4)].map((_, idx) => (
                        <Skeleton
                          key={`subject-row-skeleton-${idx}`}
                          variant="rounded"
                          height={46}
                          sx={{ borderRadius: "14px" }}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                  <Skeleton variant="rounded" width={140} height={46} sx={{ borderRadius: "16px" }} />
                  <Skeleton variant="rounded" width={140} height={46} sx={{ borderRadius: "16px" }} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4} lg={3}>
              <Box className="learner-edit-sidebar">
                <Box className="side-card" sx={{ p: 2 }}>
                  <Skeleton variant="text" width="62%" height={48} />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.45 }}>
                    {[...Array(5)].map((_, idx) => (
                      <Box
                        key={`keywords-skeleton-${idx}`}
                        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                      >
                        <Skeleton variant="text" width="72%" height={30} />
                        <Skeleton variant="text" width={14} height={24} />
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box className="side-card" sx={{ p: 2 }}>
                  <Skeleton variant="text" width="76%" height={48} />
                  <Skeleton variant="rounded" height={180} sx={{ borderRadius: "14px", mb: 1 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Skeleton variant="text" width={94} height={26} />
                    <Skeleton variant="rounded" width={136} height={36} sx={{ borderRadius: "999px" }} />
                  </Box>
                </Box>

                <Box className="side-card side-card--danger" sx={{ p: 2 }}>
                  <Skeleton variant="text" width="66%" height={42} />
                  <Skeleton variant="text" width="94%" height={24} />
                  <Skeleton variant="text" width="85%" height={24} sx={{ mb: 1 }} />
                  <Skeleton variant="rounded" width="100%" height={42} sx={{ borderRadius: "999px" }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default EditUserDetailsSkeleton;
